import { useIntl } from '@capsicum/common';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type FieldError } from 'react-hook-form';

import { useRPPostBidQueries } from '../../data-access/queries';

type UseRPBidAmountProps = {
	mainCurrencyShortcode: string;
	minimumBid: number;
	userAmount: number;
};
// would be nice to have a better mapping for this but BE use id for error code
export const REDEEM_LIMIT_ERROR_CODE = 'b101872c-475b-4b9b-9f9c-5a1172980b47';

export const useRPBid = ({ mainCurrencyShortcode, minimumBid, userAmount }: UseRPBidAmountProps) => {
	const { $t } = useIntl();
	const [amount, setAmount] = useState<number>(minimumBid);

	useEffect(() => {
		if (minimumBid) {
			setAmount(minimumBid);
		}
	}, [minimumBid]);

	const texts = useMemo(
		() => ({
			buttonText: $t('rewards.rpAuctions.placeABid'),
			enterBid: $t('rewards.rpAuctions.bid.enterBid'),
			legalHubUrl: $t('rewards.auctions.bids.drawer.error.redeemLimitExceeded.legalHubUrl'),
			lessThanMinimumError: (minBidAmount: number) =>
				$t('rewards.rpAuctions.bid.lessThanMinimumError', {
					minimumBid: `${minBidAmount} ${mainCurrencyShortcode}`
				}),
			notEnoughRP: $t('rewards.rpAuctions.bid.notEnoughRP'),
			placeABid: $t('rewards.rpAuctions.placeABid'),
			redeemLimitExceededDescription: $t('rewards.auctions.bids.drawer.error.redeemLimitExceeded.description'),
			redeemLimitExceededPrimaryButton: $t(
				'rewards.auctions.bids.drawer.error.redeemLimitExceeded.primaryButtonText'
			),
			redeemLimitExceededSecondaryButton: $t(
				'rewards.auctions.bids.drawer.error.redeemLimitExceeded.secondaryButtonText'
			),
			redeemLimitExceededTitle: $t('rewards.auctions.bids.drawer.error.redeemLimitExceeded.title'),
			rewardPoints: $t('rewards.rpAuctions.rewardPoints'),
			warning: $t('rewards.rpAuctions.bid.warning')
		}),
		[$t, mainCurrencyShortcode]
	);

	const validateAmount = useCallback(
		(bidAmount: number, minBidAmount: number, walletAmount: number) => {
			if (bidAmount < minBidAmount) {
				return {
					message: texts.lessThanMinimumError(minBidAmount),
					type: 'required'
				} as FieldError;
			} else if (bidAmount > walletAmount) {
				return {
					message: texts.notEnoughRP,
					type: 'required'
				} as FieldError;
			}

			return undefined;
		},
		[amount, minimumBid, userAmount]
	);

	const {
		data: postRPBidData,
		isPending: isPostRPBidPending,
		mutateAsync: postRPBidMutateAsync
	} = useRPPostBidQueries();

	return {
		amount,
		isPostRPBidPending,
		postRPBidData,
		postRPBidMutateAsync,
		setAmount,
		texts,
		validateAmount
	};
};
